import { RefObject, useEffect, useRef, useState } from "react";

export interface Fullscreen {
  ref: RefObject<HTMLDivElement>;
  isFullscreen: boolean;
  isFallbackFullscreen: boolean;
  toggleFullscreen: () => void;
}

/**
 * Shows the referenced element alone on the whole screen. Uses the Fullscreen API and falls back
 * to covering the viewport with CSS where it is missing or refused (iPhone Safari, no user gesture).
 */
export function useFullscreen(): Fullscreen {
  const ref = useRef<HTMLDivElement>(null);
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
  const [isFallbackFullscreen, setIsFallbackFullscreen] = useState(false);
  const isFullscreen = isNativeFullscreen || isFallbackFullscreen;

  useEffect(() => {
    const handler = () =>
      setIsNativeFullscreen(
        ref.current !== null && document.fullscreenElement === ref.current,
      );
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  function exitFullscreen() {
    if (document.fullscreenElement === ref.current)
      void document.exitFullscreen();
    setIsFallbackFullscreen(false);
  }

  function enterFullscreen() {
    const element = ref.current;
    if (element === null) return;
    if (document.fullscreenEnabled)
      element.requestFullscreen().catch(() => setIsFallbackFullscreen(true));
    else setIsFallbackFullscreen(true);
  }

  function toggleFullscreen() {
    if (isFullscreen) exitFullscreen();
    else enterFullscreen();
  }

  return { ref, isFullscreen, isFallbackFullscreen, toggleFullscreen };
}
